import { useEffect, useState, useCallback } from "react";
import Banner from "./banner/Banner";
const Filter = dynamic(() => import("./filter/Filter"), { ssr: false });
const TechCard = dynamic(() => import("./tech-card/TechCard"), { ssr: false });
import QueryString from "qs";
import apiService from "@/common/services/apiService";
import { throttle } from "lodash";
import dynamic from "next/dynamic";
const FiltersPanel = dynamic(() => import("./filter-mobile/FilterMobile"), {
  ssr: true,
});

const CaseStudies: React.FC<any> = ({ data }) => {
  const [page, setPage] = useState(1);
  const [maxPages, setMaxPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [caseStudies, setCaseStudies] = useState<any>([]);
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const [availableFilters, setAvailableFilters] = useState<any>({
    industries: [],
    projectTypes: [],
    technologies: [],
  });
  const [selectedFilters, setSelectedFilters] = useState<any>({
    industries: [],
    projectTypes: [],
    technologies: [],
  });

  const blocks = data?.blocks || [];
  const breadcrumbs = data?.breadcrumbs;
  const bannerBlock = blocks.find(
    (block: any) => block.__component === "blocks.banner"
  );
  const testimonial = blocks.find(
    (block: any) => block.__component === "blocks.testimonials"
  );

  const bookConsultant = blocks.find(
    (block: any) => block.__component === "blocks.book-consultations-multiple"
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    setIsMobile(mediaQuery.matches);

    const handleResize = () => setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleResize);

    return () => mediaQuery.removeEventListener("change", handleResize);
  }, []);

  useEffect(() => {
    fetchAvailableFilters();
  }, []);

  const fetchAvailableFilters = async () => {
    try {
      const [industries, projectTypes, technologies] = await Promise.all([
        apiService.getapi1(`case-study-industries?pagination[limit]=-1`),
        apiService.getapi1(`case-study-project-types?pagination[limit]=-1`),
        apiService.getapi1(`case-study-technologies?pagination[limit]=-1`),
      ]);

      setAvailableFilters({
        industries: [
          ...new Set(
            industries.data.data.map(({ attributes }: any) => ({
              name: attributes.name,
              slug: attributes.sulg || attributes.slug,
            }))
          ),
        ],
        projectTypes: [
          ...new Set(
            projectTypes.data.data.map(({ attributes }: any) => ({
              name: attributes.name,
              slug: attributes.slug,
            }))
          ),
        ],
        technologies: [
          ...new Set(
            technologies.data.data.map(({ attributes }: any) => ({
              name: attributes.name,
              slug: attributes.slug,
            }))
          ),
        ],
      });
    } catch (error) {
      console.error("Error fetching available filters:", error);
    }
  };

  const normalizeCaseStudies = (data: any) => {
    return data.map((study: any) => ({
      ...study,
      attributes: {
        ...study.attributes,
        industries: (study?.attributes?.case_study_industries?.data || []).map(
          (i: any) =>
            i?.attributes?.slug?.toLowerCase() ||
            i?.attributes?.sulg?.toLowerCase() ||
            ""
        ),
        projectTypes: (
          study?.attributes?.case_study_project_types?.data || []
        ).map((p: any) => p?.attributes?.slug?.toLowerCase() || ""),
        technologies_new:
          study?.attributes?.case_study_technologies?.data || [],
        technologies: (
          study?.attributes?.case_study_technologies?.data || []
        ).map((t: any) => t?.attributes?.slug?.toLowerCase() || ""),
      },
    }));
  };

  const fetchCaseStudies = async () => {
    try {
      setLoading(true);
      const filters: any = { slug: { $ne: "case-studies" }, $and: [] };
      if (selectedFilters?.industries?.length) {
        filters.$and.push({
          case_study_industries: {
            sulg: {
              $in: selectedFilters.industries.map((i: any) => i.sulg || i.slug),
            },
          },
        });
      }

      if (selectedFilters?.projectTypes?.length) {
        filters.$and.push({
          case_study_project_types: {
            slug: {
              $in: selectedFilters.projectTypes.map((p: any) => p.slug),
            },
          },
        });
      }

      if (selectedFilters?.technologies?.length) {
        filters.$and.push({
          case_study_technologies: {
            slug: {
              $in: selectedFilters.technologies.map((t: any) => t.slug),
            },
          },
        });
      }

      const pageSize = 3;

      const queryParams = {
        populate: {
          image: { fields: ["url"] },
          case_study_industries: { fields: ["name", "sulg", "slug"] },
          case_study_project_types: { fields: ["name", "slug"] },
          // case_study_technologies: { fields: ["name", "slug", "image"] }
          case_study_technologies: {
            fields: ["name", "slug"],
            populate: {
              image: {
                fields: ["url", "name", "alternativeText", "width", "height"],
              },
            },
          },
        },
        // populate: "deep, 5",
        pagination: { page, pageSize },
        sort: { publishedAt: "DESC" },
        filters,
      };

      const apiUrl = `case-studies-v2?${QueryString.stringify(queryParams, {
        encodeValuesOnly: true,
      })}`;

      const response = await apiService.getapi1(apiUrl);
      // const newData = response?.data?.data || [];

      const newData = normalizeCaseStudies(response?.data?.data || []);
      setCaseStudies((prev: any) => {
        const mergedData = [...prev, ...newData];

        // Remove duplicates based on slug or ID
        const uniqueData = Array.from(
          new Map(
            mergedData.map((item) => [
              item.attributes.slug || item.attributes.sulg,
              item,
            ])
          ).values()
        );

        return uniqueData;
      });

      setMaxPages(response?.data?.meta?.pagination?.pageCount || 1);
      setHasMore(newData.length > 0);
    } catch (error) {
      console.error("Error fetching case studies:", error);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleScroll = useCallback(
    throttle(() => {
      const productDiv = document.getElementById("case-studies-container");
      if (productDiv) {
        const { scrollTop, clientHeight } = document.documentElement;
        const flag = scrollTop + clientHeight >= productDiv.offsetHeight - 350;
        if (flag && !loading && hasMore)
          setPage((prev) => (prev + 1 >= maxPages ? maxPages : prev + 1));
      }
    }, 200),
    [loading, hasMore, selectedFilters]
  );

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (page <= maxPages && hasMore) {
      fetchCaseStudies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedFilters]);

  useEffect(() => {
    if (isFilterChanged) window.scrollTo(0, 200);
  }, [isFilterChanged, isChanged]);

  const handleFilterChange = (newFilters: any) => {
    setHasMore(true);
    setPage(1);
    setCaseStudies([]);
    setSelectedFilters(newFilters);
    setIsChanged(!isChanged);
    setIsFilterChanged(true);
  };

  const removeFilter = (category: any, filter: any) => {
    handleFilterChange({
      ...selectedFilters,
      [category]: selectedFilters[category].filter(
        (item: any) => item.slug !== filter.slug
      ),
    });
  };

  return (
    <div className="min-h-screen">
      {bannerBlock && <Banner block={bannerBlock} breadcrumbs={breadcrumbs} />}
      {isMobile ? (
        <div className="relative z-10">
          <FiltersPanel
            isChanged={isChanged}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            availableFilters={availableFilters}
          />
        </div>
      ) : (
        <div className="-translate-y-full md:relative z-10">
          <Filter
            isChanged={isChanged}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            availableFilters={availableFilters}
          />
        </div>
      )}
      <div className="container">
        {Object.values(selectedFilters).some(
          (filters: any) => filters.length > 0
        ) && (
            <div className="mt-1">
              <ul className="flex flex-wrap gap-3">
                {Object.entries(selectedFilters).flatMap(
                  ([category, filters]: any) =>
                    filters?.map((filter: any, i: any) => (
                      <li
                        key={`${category}-${i}`}
                        className="flex items-center bg-blue-100 text-blue-800 px-3 pt-[11px] pb-[9px] rounded-full text-sm font-medium"
                      >
                        {filter.slug ?? ""}
                        <button
                          onClick={() => removeFilter(category, filter)}
                          className="ml-2 hover:text-red-600 cursor-pointer"
                          aria-label={`Remove ${filter.name}`}
                        >
                          ✕
                        </button>
                      </li>
                    ))
                )}
              </ul>
            </div>
          )}
      </div>
      <TechCard
        testimonial={testimonial}
        bookConsultant={bookConsultant}
        caseStudies={caseStudies}
        loading={loading}
      />
    </div>
  );
};

export default CaseStudies;
